import {
  FormControl,
  FormControlLabel,
  Radio,
  RadioGroup,
  TextField,
  Tooltip,
} from '@material-ui/core'
import { Autocomplete } from '@material-ui/lab'
import { Button } from 'components/facets'
import { ArticleAttributes } from 'components/facets/ArticleAttributes'
import ImagePopover from 'components/facets/ImagePopover'
import { getGameCodeFromName } from 'lib/utils'
import _ from 'lodash'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { AutoSizer, Column, Table } from 'react-virtualized'
import { generateArticleKey, getGameFromId, InventoryArticle, Location } from 'shared'
import { ESCAPE_STRING_FOR_EMPTY_COMMENT } from 'utils/constants'
import { getProductImageUrl } from 'utils/imageUtils'
import { EditLocationsDialog } from './EditLocationsDialog'
import { getCommentsMapForeachInventoryArticle, InventoryCommentsMap } from './onboarding-utils'

export interface InventoryCommentsAndLocationsTableProps {
  fetchedStock: InventoryArticle[]
  onConfirm: (updatedInventory: InventoryArticle[]) => void
  // The wrappers below belong to the onboarding page: in a dialog they only add
  // its margins and its top padding.
  withPageLayout?: boolean
}

export const InventoryCommentsAndLocationsTable = ({
  fetchedStock,
  onConfirm,
  withPageLayout = true,
}: InventoryCommentsAndLocationsTableProps): JSX.Element => {
  const { t } = useTranslation()
  const commentsMapForeachInventoryArticleGroupedByLocations =
    getCommentsMapForeachInventoryArticle(fetchedStock)
  const [mapArticlesWithOnlyOneComment, setMapArticlesWithOnlyOneComment] =
    useState<InventoryCommentsMap>({})

  const [articlesWithMoreThanOneComment, setArticlesWithMoreThanOneComment] =
    useState<InventoryCommentsMap>({})

  const [checkedQuestions, setCheckedQuestions] = useState<'comments' | 'locations' | 'both'>(
    'both'
  )

  useEffect(() => {
    const listOne: InventoryCommentsMap = {}
    const listTwo: InventoryCommentsMap = {}
    Object.keys(commentsMapForeachInventoryArticleGroupedByLocations).forEach((key) => {
      const { article, comments } = commentsMapForeachInventoryArticleGroupedByLocations[key]

      if (comments.length > 1) {
        listOne[key] = { article, comments }
      } else {
        listTwo[key] = { article, comments }
      }
    })

    setArticlesWithMoreThanOneComment(listOne)
    setMapArticlesWithOnlyOneComment(listTwo)
  }, [])

  const handleCheckQuestion = (event: React.ChangeEvent<HTMLInputElement>) => {
    setCheckedQuestions(event.target.value as 'comments' | 'locations' | 'both')

    // save user choice:
    const copyOfArticlesWithOneComment = _.cloneDeep(mapArticlesWithOnlyOneComment)
    Object.keys(copyOfArticlesWithOneComment).forEach((key) => {
      const { article, comments } = copyOfArticlesWithOneComment[key]

      /* if the user wants to use the comments only as comments then I white the location
        if the user wants to use the comments as locations then I white the comment
        if the user wants to use the comments as both then I put the comment both as a location and as a comment. 
        I do this because it is possible that the user has modified the check and therefore some information could have been lost
        in this case I only select locations[0] because if I had only 1 comment then I have only 1 location */

      if (event.target.value === 'comments') {
        article.comment = comments[0]
        article.locations![0].name = ''
      } else if (event.target.value === 'locations') {
        article.comment = ESCAPE_STRING_FOR_EMPTY_COMMENT
      } else if (event.target.value === 'both') {
        article.comment = comments[0]
        article.locations![0].name = comments[0]
      }
    })
    setMapArticlesWithOnlyOneComment(copyOfArticlesWithOneComment)
  }

  const updateSingleRowComment = (article: InventoryArticle, selectedComment: string) => {
    selectedComment
    const key = generateArticleKey(article)

    const copyOfWorkingMap = _.cloneDeep(articlesWithMoreThanOneComment)
    const entry = copyOfWorkingMap[key]
    if (entry) {
      entry.article.comment = selectedComment
    }
    setArticlesWithMoreThanOneComment(copyOfWorkingMap)
  }

  /**
   * Once the user has applied the prices they want, the WorkingMapOfFetchedStock is converted to a list
   * and returned to OnboardingPresenter to publish the changes
   */
  const confirmUpdates = () => {
    const listOfUpdatedArticles: InventoryArticle[] = []
    Object.keys(articlesWithMoreThanOneComment).forEach((key) => {
      const { article } = articlesWithMoreThanOneComment[key]
      listOfUpdatedArticles.push(article)
    })

    Object.keys(mapArticlesWithOnlyOneComment).forEach((key) => {
      const { article } = mapArticlesWithOnlyOneComment[key]
      listOfUpdatedArticles.push(article)
    })

    onConfirm(listOfUpdatedArticles)
  }

  return (
    <>
      <div
        className={withPageLayout ? 'd-flex justify-content-center w-100 onboarding-container' : ''}
      >
        <div className={withPageLayout ? 'wizard-content container' : ''}>
          <h3 className="fw-normal">{t('onboarding.selectCommentsAndLocations')}</h3>
          <p className="fs-6 fw-light">
            {t('onboarding.articlesWithOneComment', {
              count: Object.keys(mapArticlesWithOnlyOneComment).length,
            })}
          </p>
          <FormControl>
            <RadioGroup value={checkedQuestions} onChange={handleCheckQuestion}>
              <FormControlLabel
                checked={checkedQuestions === 'both'}
                value="both"
                control={<Radio />}
                className="fw-light"
                label={t('onboarding.keepAsBoth')}
                data-testid="check-both"
              />
              <FormControlLabel
                checked={checkedQuestions === 'comments'}
                value="comments"
                className="fw-light"
                control={<Radio />}
                label={t('onboarding.keepAsComment')}
                data-testid="check-comments"
              />
              <FormControlLabel
                checked={checkedQuestions === 'locations'}
                value="locations"
                className="fw-light"
                control={<Radio />}
                label={t('onboarding.keepAsLocation')}
                data-testid="check-locations"
              />
            </RadioGroup>
          </FormControl>
          <div className="w-100 d-flex justify-content-end mb-4">
            <Button variant="contained" onClick={confirmUpdates} data-testid="confirm-prices">
              {t('onboarding.confirmAndSave')}
            </Button>
          </div>
          <InventoryTable
            articlesWithMoreThanOneComment={articlesWithMoreThanOneComment}
            setArticlesWithMoreThanOneComment={setArticlesWithMoreThanOneComment}
            updateSingleRowComment={updateSingleRowComment}
          />
        </div>
      </div>
    </>
  )
}

interface InventoryTableProps {
  articlesWithMoreThanOneComment: InventoryCommentsMap
  setArticlesWithMoreThanOneComment: React.Dispatch<React.SetStateAction<InventoryCommentsMap>>
  updateSingleRowComment: (article: InventoryArticle, selectedComment: string) => void
}

const InventoryTable: React.FC<InventoryTableProps> = ({
  articlesWithMoreThanOneComment,
  setArticlesWithMoreThanOneComment,
  updateSingleRowComment,
}) => {
  const { t } = useTranslation()
  const [openLocationsPanel, setOpenLocationsPanel] = useState(false)
  const [currentArticle, setCurrentArticle] = useState<InventoryArticle | undefined>(undefined)
  const [dataForTable, setDataForTable] = useState<
    {
      article: InventoryArticle
      comments: string[]
    }[]
  >([])

  useEffect(() => {
    const tableData = Object.keys(articlesWithMoreThanOneComment).map((key) => {
      const { article, comments } = articlesWithMoreThanOneComment[key]
      return {
        article: article,
        comments,
      }
    })

    setDataForTable(tableData)
  }, [articlesWithMoreThanOneComment])

  const handleConfirmEditLocations = (changes: Location[]) => {
    const copyOfArticlesWithMoreComments = _.cloneDeep(articlesWithMoreThanOneComment)
    const key = generateArticleKey(currentArticle!)
    copyOfArticlesWithMoreComments[key].article.locations! = [...changes]
    setArticlesWithMoreThanOneComment(copyOfArticlesWithMoreComments)
    handleCloseEditLocations()
    setCurrentArticle(undefined)
  }

  const handleCloseEditLocations = () => {
    setOpenLocationsPanel(false)
  }

  return (
    <div
      style={{
        height:
          dataForTable.length === 0 ? 'auto' : `${Math.min(400, dataForTable.length * 50 + 90)}px`,
        width: '100%',
      }}
    >
      {dataForTable.length === 0 ? (
        <p className="fs-6 text-center">{t('onboarding.uniformComments')}</p>
      ) : (
        <>
          <p className="fs-6 fw-light">
            {t('onboarding.articlesWithMultipleComments', {
              count: dataForTable.length,
            })}
          </p>
          <AutoSizer>
            {({ height, width }) => (
              <Table
                width={width}
                height={height}
                headerHeight={40}
                rowHeight={50}
                rowCount={dataForTable.length}
                rowGetter={({ index }) => dataForTable[index]}
                className="onboarding-inventory-table"
              >
                <Column
                  width={55}
                  label=""
                  dataKey=""
                  disableSort={true}
                  cellRenderer={({ rowData }) => (
                    <Tooltip title={getGameFromId(rowData.article.card.idGame) || ''}>
                      <span
                        className={`ss-${getGameCodeFromName(
                          getGameFromId(rowData.article.card.idGame)
                        )} ss-game-icon ss-fw`}
                        style={{ fontSize: '1.5rem' }}
                      ></span>
                    </Tooltip>
                  )}
                />
                <Column
                  label=""
                  dataKey=""
                  width={60}
                  cellRenderer={({ rowData }) => (
                    <ImagePopover
                      variant="iconPopover"
                      imageComponent={
                        <img
                          width="62px"
                          src={getProductImageUrl(rowData.article.card._id.toString())}
                        />
                      }
                      imageId={rowData.article.card._id.toString()}
                    />
                  )}
                />
                <Column
                  label="Name"
                  dataKey="article.card.name"
                  width={200}
                  cellRenderer={({ rowData }) => <span>{rowData.article.card.name}</span>}
                />
                <Column
                  label="Cn"
                  dataKey="article.card.cn"
                  width={60}
                  cellRenderer={({ rowData }) => <span>{rowData.article.card.cn}</span>}
                />
                <Column
                  label="Expansion"
                  dataKey="article.card.expansion"
                  width={200}
                  cellRenderer={({ rowData }) => <span>{rowData.article.card.expansion}</span>}
                />
                <Column
                  label="Attributes"
                  dataKey="article.attributes"
                  width={200}
                  cellRenderer={({ rowData, rowIndex }) => (
                    <div>
                      <ArticleAttributes
                        article={rowData.article}
                        identifierForDataTestId={rowIndex.toString()}
                        iconHeight={17}
                      />
                    </div>
                  )}
                />

                <Column
                  label="Select comment"
                  dataKey="comment"
                  width={250}
                  cellRenderer={({ rowData, rowIndex }) => (
                    <Autocomplete
                      data-testid={'select-comment-' + rowIndex}
                      value={
                        rowData.article.comment === ESCAPE_STRING_FOR_EMPTY_COMMENT
                          ? ''
                          : rowData.article.comment
                      }
                      onChange={(_event, newValue) => {
                        if (newValue) {
                          updateSingleRowComment(rowData.article, newValue)
                        }
                      }}
                      onInputChange={(_event, newInputValue) => {
                        if (newInputValue === '') {
                          updateSingleRowComment(rowData.article, ESCAPE_STRING_FOR_EMPTY_COMMENT) // If the user deletes, whiten the value
                        } else updateSingleRowComment(rowData.article, newInputValue)
                      }}
                      freeSolo
                      options={rowData.comments} // List of available comments
                      getOptionLabel={(option) => option} // Convert each option to string
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="Select or write a comment"
                          variant="standard"
                        />
                      )}
                    />
                  )}
                />

                <Column
                  label="Locations"
                  dataKey="article.locations"
                  width={200}
                  cellRenderer={({ rowData, rowIndex }) => (
                    <>
                      <span className="me-3">{rowData.article.attributes[0]}x</span>
                      <Button
                        variant="outlined"
                        data-testid={'edit-locations-' + rowIndex}
                        onClick={() => {
                          setCurrentArticle(
                            articlesWithMoreThanOneComment[generateArticleKey(rowData.article)]
                              .article
                          )
                          setOpenLocationsPanel(true)
                        }}
                      >
                        <i className="fa-regular fa-pen"></i>
                      </Button>
                    </>
                  )}
                />
              </Table>
            )}
          </AutoSizer>
        </>
      )}
      {openLocationsPanel && (
        <EditLocationsDialog
          article={currentArticle!}
          open={openLocationsPanel}
          handleConfirm={handleConfirmEditLocations}
          handleClose={handleCloseEditLocations}
        />
      )}
    </div>
  )
}
